// ================= HOME =================
//
// WHAT THE ROOT URL SHOWS BEFORE ANYONE HAS ASKED TO PLAY.
//
// The sitewide usability audit's second P0: the root was the setup screen, which it called "an
// efficient returning-player lobby, but a weak public landing page" -- six jobs at once (two names,
// two deck selects, a thirty-card preview, the daily challenge, account copy and links out) with no
// sentence anywhere saying what the game is. A first-time visitor arrived inside a tool.
//
// So Home explains and orients, and the setup screen keeps its real job one tap behind it as the
// match lobby. Nothing about setup changes; it is reached rather than landed on.
//
// A SCREEN, NOT A PAGE. It renders into the same four zones as every other screen (see
// js/ui-shell.js) and there is no home.html and no /home route. That was a decision, not an
// omission: this is a no-build static site where Cloudflare Pages answers missing paths with
// index.html at status 200, the OAuth return allowlist in lib/accounts/session.js is anchored on
// "/", and every invite link is `/?room=CODE`. A second entry point would have to be taught to all
// three. The audit allows it in as many words -- "URL migration can be gradual".
//
// WHAT IT DOES NOT OWN. It draws no panel of its own that another screen already draws: the daily
// challenge, the credit summary and the online entry are all UIInternals helpers shared with the
// setup rail, so there is exactly one copy of each and they cannot drift apart.
window.UIHome = (function () {
  'use strict';

  const H = window.UIInternals;
  const workspaceEl = H.workspaceEl;
  const escapeHtml = H.escapeHtml;
  const announce = H.announce;
  const onFlow = H.onFlow;

  // The two cards the "how one exchange works" strip is drawn with. TAKEN FROM A DECK RATHER THAN
  // NAMED, because a card id written here is one that can be renamed or retired without anything
  // failing loudly -- the strip would simply render two blanks. A deck is a much larger thing to
  // lose quietly, and outside_reader is the one every other screen already treats as the default.
  function demoCards() {
    const deck = (window.DECKS && window.DECKS.outside_reader) || [];
    const seen = [];
    for (let i = 0; i < deck.length && seen.length < 2; i++) {
      if (seen.indexOf(deck[i]) < 0 && window.CARDS[deck[i]]) seen.push(deck[i]);
    }
    return seen;
  }

  function tile(id, opts) {
    return window.CardTile.compactCardHtml(id, Object.assign({ readonly: true, moveArt: true }, opts || {}));
  }

  // ---- has this person played before? ------------------------------------------------------------
  //
  // The explainer is the reason Home exists -- new players reported that it is what made the
  // two-card idea land -- and it is also two thirds of the page. A player who opens the daily
  // challenge every morning was scrolling past the whole tutorial every morning to reach it.
  //
  // So it stays whole and stops being unfolded. Never played: expanded, exactly as before. Played
  // before: a one-line summary they can open, and if they DO open it that is remembered, because a
  // player who wants it back should not have to ask twice.
  //
  // A BOUT STARTED, not an account. A guest who has played twenty bouts is not a new player, and a
  // first-time visitor who signs in immediately still is -- so the signal is the thing that
  // actually happened. Written by js/ui.js's startBout through markPlayed below, which is the only
  // place that knows a bout began at all.
  const PLAYED_KEY = 'sc.home.played';
  const OPEN_KEY = 'sc.home.loopOpen';

  // WHAT THIS GAME IS, IN ONE LINE, and it lives here because Home is the screen whose whole job
  // is answering that. It was written for Zone 4's instruction slot on this screen; it is exported
  // because a SECOND surface needs the same sentence and must not own a second copy of it.
  //
  // That surface is js/ui-online.js's sign-in card -- the screen a signed-out friend lands on when
  // they open an invite link, which that file's own comment calls "the most common first touch this
  // feature has". It opened by demanding an account from somebody who had not yet been told what
  // they were being asked to sign up for. Two copies of a pitch is two pitches, and the one nobody
  // is looking at is the one that goes stale.
  const TAGLINE = 'A one-on-one card game about reading your opponent.';

  // Every access guarded: localStorage THROWS rather than returning null in a Safari private
  // window and in some embedded webviews, and a landing page that will not render because it could
  // not read a preference is a worse failure than one that always explains itself.
  function store(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, value);
    } catch (e) { /* no storage: every reader below treats that as "new player" */ }
    return null;
  }

  function hasPlayed() { return store(PLAYED_KEY) === '1'; }
  function markPlayed() { store(PLAYED_KEY, '1'); }

  // ---- and has this person BEATEN the AI? --------------------------------------------------------
  //
  // A second, harder flag, and the difficulty ladder's default rests on it: a device that has never
  // won a vs-AI bout opens on the light tier, and after the first win the default becomes full
  // strength. Starting a bout is not enough for that — someone can start twenty bouts against Jimmy
  // and lose all twenty, and moving them up would be the game deciding they had learned something
  // the record says they have not.
  //
  // It lives here rather than in js/ui.js for the same reason PLAYED_KEY does: this module owns the
  // guarded-storage helper and the "who is this player" questions, and the alternative was a second
  // try/catch wrapper in a file that has no other reason to touch localStorage.
  //
  // ANY vs-AI WIN COUNTS, including a Daily Challenge boss — which is the hardest opponent in the
  // game and would be a perverse thing to not count. Written from js/ui.js's renderResult, beside
  // the credit, because that is where a result is known.
  const WON_AI_KEY = 'sc.home.wonAI';
  function hasWonAI() { return store(WON_AI_KEY) === '1'; }
  function markWonAI() { store(WON_AI_KEY, '1'); }

  // Expanded unless we know they have played AND they have not since asked for it back.
  function loopOpen() {
    if (!hasPlayed()) return true;
    return store(OPEN_KEY) === '1';
  }

  // Called from the summary's own toggle. Records the preference either way, so closing it again
  // sticks too.
  function setLoopOpen(open) { store(OPEN_KEY, open ? '1' : '0'); }

  // ---- the sections ------------------------------------------------------------------------------

  // ANSWER "WHAT IS THIS" IN ONE SENTENCE, before any configuration. The audit's own wording,
  // because it is the clearest statement of the loop anybody has written for this game: the fact
  // that every card is both an attack and a defence is the whole idea, and it was nowhere on the
  // old landing screen.
  // ---- the ringside art ---------------------------------------------------------------------------
  //
  // From the home-ux-art-mockups package, direction 02 "Cards in the Corner": a first-person
  // pre-exchange view, two technique cards held up, an opponent waiting across the ring. It is the
  // one image that joins the sparring fantasy to the game's defining choice, and the blue and gold
  // on those two cards are this game's own p1/p2 colours rather than a stock palette.
  //
  // BOTH TREATMENTS ARE ALWAYS IN THE DOM and the stylesheet picks between them. There are two
  // (a masthead behind the headline for a first visit, a full band beneath Explore for a returning
  // player) and three cases, because a phone gets neither of the desktop ones — the third case is a
  // viewport question, and a viewport question answered in JS is answered once at render and then
  // wrong the moment the window changes. `.is-returning` on the screen is the state half; the
  // stylesheet owns the rest.
  //
  // DECORATIVE, and marked so. The image carries no information the page does not state in words
  // directly beside it, which is exactly the test for aria-hidden — a screen reader that announced
  // "image" here would be announcing the wallpaper.
  //
  // Backgrounds rather than <img>, on the art director's own instruction: "treat the artwork as a
  // responsive background layer, never as content that changes panel geometry." That also keeps
  // both files out of the markup and in styles.css, which is where tools/build-dist.js's asset
  // scraper looks for `url(...)` — see referencedByCss there.
  function heroArtHtml() {
    return '<div class="home-hero-art" aria-hidden="true"></div>';
  }
  function artHtml() {
    return '<div class="home-art" aria-hidden="true"></div>';
  }

  function heroHtml() {
    return `
      <section class="home-hero">
        ${heroArtHtml()}
        <h2>Choose two. Reveal together. Outsmart.</h2>
        <p class="home-promise">Every card attacks and defends. Pick two, read the opponent, and
          decide which role each technique will play.</p>
      </section>`;
  }

  // TODAY'S CHALLENGE, in Zone 2 rather than the rail. On the setup screen it lives in Zone 3,
  // which on a phone is behind a toggle -- acceptable for a screen you reached on purpose, wrong
  // for the one thing on the landing page with a reason to come back tomorrow.
  //
  // Renders to nothing at all until /api/daily answers, which is DailyChallenge's own rule and not
  // a decision taken here: a panel that appears and then rearranges itself is worse than one that
  // arrives whole. `deckSource: 'none'` because Home has no deck select to read -- see the note on
  // playChallenge for why the rows are honest anyway.
  function challengeHtml() {
    const inner = H.dailyRailHtml({ onTier: 'UIHome.playChallenge', deckSource: 'none' });
    return inner ? `<section class="home-daily">${inner}</section>` : '';
  }

  // THREE BEATS, DRAWN WITH REAL CARDS. The audit asked for this specifically -- "use real card
  // art, not rules prose" -- and it is the difference between a landing page that describes a game
  // and one that shows it. The cards are the same renderer the hand rack uses, so what a visitor
  // sees here is literally what they will be looking at thirty seconds later.
  function explainerHtml() {
    const ids = demoCards();
    if (ids.length < 2) return '';
    // A <details> rather than a class toggle and a button: it is exactly a disclosure, the element
    // carries the open/closed semantics for assistive technology without any aria- attributes, and
    // it works with no script if this module's handler ever fails to bind.
    const open = loopOpen();
    return `
      <section class="home-loop" id="home-loop">
        <details class="home-loop-fold"${open ? ' open' : ''}>
          <summary><span class="home-loop-title">How one exchange works</span></summary>
        <ol class="home-beats">
          <li class="home-beat">
            <div class="home-beat-text">
              <span class="home-beat-n">1</span>
              <h4>Pick two</h4>
              <p>Five cards in hand. You commit to two of them, in secret.</p>
              <!-- CYCLING (RULES.md §2), and it belongs in BEAT 1 for the same reason the
                   Positioning Intent belongs in beat 2: this beat describes the Select screen and
                   the marks are made on the Select screen. A fourth beat is also not available --
                   three is the shape this explainer was built around and tests/ui-home-test.js
                   pins the count twice, once fresh and once after the fold.

                   IT NAMES THE COST IN THE SAME BREATH, which is the whole reason the rule needs
                   no printed penalty. A player told they may throw cards away and not told what
                   that eventually costs has been told half a rule, and the half they were not
                   told is the one that decides whether the first half is a good idea. -->
              <p>Any of the other three can be marked to throw away at the same time. Your hand
                always refills to five, so cycling is how you dig for the card you need &mdash;
                until you reach the bottom of your deck, which reshuffles it and costs 1 Stamina.</p>
            </div>
            <div class="home-beat-vis">
              <div class="home-beat-cards">${tile(ids[0], { selected: true })}${tile(ids[1], { selected: true })}</div>
            </div>
          </li>
          <li class="home-beat">
            <div class="home-beat-text">
              <span class="home-beat-n">2</span>
              <h4>Assign roles</h4>
              <p>One becomes your attack, the other your defence. Every card can do either.</p>
              <!-- THE THIRD SECRET COMMITMENT, which used to arrive completely cold at the first
                   Commit screen. A player who has read this explainer knew about two decisions and
                   then met three, and the one nobody had named is the one that decides the range
                   every card is then judged at.

                   IN BEAT 2, NOT A FOURTH BEAT. Three is the shape this explainer was designed
                   around and tests/ui-home-test.js pins the count twice -- once fresh, once after
                   the fold. Beat 2 is where it belongs anyway: this beat describes the Commit
                   screen, and the Intent is committed on the Commit screen. In beat 1 it would
                   read as part of the deal.

                   THE THREE VERBS PARAPHRASE THE ENGINE rather than inventing a metaphor for it:
                   closer / hold / farther is INTENT_PULL (js/ui-resolution.js), which mirrors
                   engine.js's INTENT_CLASH_RESULT. Copy that restates a rule in its own words is a
                   second statement of that rule, and the second one is the one that goes stale. -->
              <p>You also commit a Positioning Intent — Pressure closes in, Anchor holds ground,
                Evade slips away — and the two Intents fight over the range.</p>
            </div>
            <div class="home-beat-vis">
              <div class="home-beat-cards">
                <div class="home-role"><span class="home-role-tag atk">Attack</span>${tile(ids[0])}</div>
                <div class="home-role"><span class="home-role-tag def">Defence</span>${tile(ids[1])}</div>
              </div>
            </div>
          </li>
          <li class="home-beat">
            <div class="home-beat-text">
              <span class="home-beat-n">3</span>
              <h4>Resolve</h4>
              <!-- AND THE SENTENCE THAT SAYS HOW TO WIN, which was nowhere a player could read.
                   RULES.md:24-26 has always stated it and no page links to RULES.md; the HUD counts
                   "Exchange N / 10" without saying what the count decides, and the Stamina bars
                   never say that empty ends it. The objective was first stated AFTER the first
                   bout, in the recap. Added to the beat that already describes what a resolution
                   does rather than as a fourth beat, because three is the shape the explainer was
                   designed around and the suite pins that count. -->
              <p>Both plans reveal at once. Range, speed and footwork decide what actually lands.
                Empty their Stamina to win by stoppage, or be ahead on it after ten exchanges.</p>
            </div>
            <div class="home-beat-vis">
              <div class="home-verdict">
                <span class="home-pill hit">Clean hit</span>
                <span class="home-pill dmg">3 Damage</span>
              </div>
            </div>
          </li>
        </ol>
        </details>
      </section>`;
  }

  // WHO YOU ARE, AND WHAT SIGNING IN IS FOR. Three states, and the first of them is silence: with
  // no accounts backend behind the page -- a file:// copy, a build with no Functions, or simply
  // before /api/me has answered -- there is nothing to say, and an invitation to sign in to
  // something that is not there is worse than no section. Same rule the library's store follows.
  function cornerHtml() {
    const a = (window.UI && window.UI.setupAccountState) ? window.UI.setupAccountState() : null;
    if (!a || !a.configured) return '';

    if (!a.signedIn) {
      const href = (window.DeckSync && window.DeckSync.SIGN_IN_URL) || '#';
      const note = (window.DeckSync && window.DeckSync.PRIVACY_NOTE) || '';
      // THE PRIVACY PARAGRAPH GOES BEHIND A DISCLOSURE. The audit asked for this by name: the full
      // paragraph is honest and worth keeping, and it should not be the third thing a first-time
      // visitor reads. One benefit sentence, then "What is stored?" for anyone who wants it.
      // "Nothing is saved" is true of decks and NOT true of bouts, which is the one thing on this
      // screen a guest has already accumulated: every bout they finish is queued to this device and
      // goes up the first time a session exists. The same sentence the setup panel shows, from the
      // same function, so the two pitches cannot say different numbers.
      const queued = H.queuedPitchHtml();
      return `
        <section class="home-corner">
          <h3>Your corner</h3>
          <p>You are playing as a guest. Everything works — nothing is saved.</p>
          <p><a class="home-signin" href="${escapeHtml(href)}">Sign in with Discord</a>
            to keep your decks, earn credits and collect the season's cards.</p>
          ${queued}
          <details class="home-privacy">
            <summary>What is stored?</summary>
            <p>${escapeHtml(note)}</p>
          </details>
        </section>`;
    }

    const credits = a.credits && a.credits.ok
      ? `<p class="home-credits"><b>${escapeHtml(String(a.credits.balance))}</b>
           credit${a.credits.balance === 1 ? '' : 's'}</p>` : '';
    const DC = window.DailyChallenge;
    const sum = DC && DC.state() ? DC.summary() : null;
    const daily = sum && !sum.allBeaten
      ? `<p class="home-daily-left">${escapeHtml(String(sum.left))} of
           ${escapeHtml(String(sum.total))} challenge credits still on the table today.</p>`
      // "A new deal", not "New decks" -- the season's three bosses are authored and fixed, so only
      // the shuffle changes at midnight. Same correction as the rail's line in js/ui.js; see the
      // comment there for why the wording has to hold in both the authored and the ranked regime.
      : (sum ? '<p class="home-daily-left">Every challenge beaten today. A new deal at midnight UTC.</p>' : '');
    return `
      <section class="home-corner">
        <h3>Your corner</h3>
        <p class="home-who">Signed in as <b>${escapeHtml(a.name || 'you')}</b>.</p>
        ${credits}${daily}
      </section>`;
  }

  // DEPTH, AFTER THE ACTION. Deliberately last: these are the places a returning player goes, and
  // the ones a first-time visitor should not be asked to consider before they have played.
  //
  // RULES IS A LINK HERE AND NOT CONTENT, which is the whole reason it is allowed on this screen
  // at all. The brief the mechanics-visibility work runs on bans a rules wall on the landing page
  // in as many words -- "Do not add a large rules wall to the landing page. Add a short setup
  // statement and then teach each mechanic the first time it actually triggers" -- and the coach
  // (js/mechanics-coach.js) is that teaching. None of that means a player with a question at 11pm
  // should have nowhere to look it up; it means the answer does not live on the way in. So the
  // reference is a destination, one tap away, sitting with the other two.
  function exploreHtml() {
    return `
      <section class="home-explore">
        <h3>Explore</h3>
        <div class="home-explore-links">
          <a class="home-card-link" href="deck-builder.html">
            <b>Decks</b><span>Build one from scratch, or clone a deck and change it.</span></a>
          <a class="home-card-link" href="library.html">
            <b>Cards</b><span>Every card in the game, and the season's shelf.</span></a>
          <a class="home-card-link" href="rules.html">
            <b>Rules</b><span>How an exchange works, and the exact rule behind every badge.</span></a>
        </div>
      </section>`;
  }

  // ---- the screen --------------------------------------------------------------------------------

  // READS THE DOM, NOT A FLAG. body.dataset.screen is written here and cleared by toPlay, but the
  // rail's Host and Join buttons leave this screen too and go straight into ui-online.js, which
  // has never heard of Home and should not have to. A flag those paths do not clear would have the
  // account repaint below drawing Home back over a join form. What is actually on screen cannot
  // go stale.
  function isShowing() {
    return !!document.querySelector('.home-screen');
  }

  // `opts.quiet` — a REPAINT rather than an arrival. Everything about the screen is rebuilt either
  // way; what quiet suppresses is the two things that are only right when a player has just
  // navigated here: scrolling to the top and announcing "Home" to a screen reader. An async fetch
  // landing (the credit summary, the daily challenge) must not yank a reader back to the top of a
  // page they are halfway down, or interrupt them to name a screen they never left. This was
  // already wrong before it had a name: the home.accountKnown subscriber below has always
  // re-rendered mid-view, and now passes quiet too.
  // REGION 3: the day's theme, carried into the empty lower rail.
  //
  // Zone 3 here is 288px of credits, record and Play online in a column measured at 449px empty
  // on a 1440x900 desktop and 987px empty at 2560x1440 -- 57% to 74% of the rail, and the largest
  // unused area on the site. Each themed set ships a 2:3 portrait companion to the 3:1 banner
  // that sits behind the challenge panel in Zone 2, drawn as a closer vignette of the same place
  // rather than a crop of the same picture. "challenge theme art/STYLE_GUIDE.md" is the brief.
  //
  // AN EMPTY DIV, AND THAT IS THE WHOLE ELEMENT. The art is a CSS background keyed off the set id
  // exactly as the banner is -- a file has to be in the build and only the client knows which file
  // an id means, which styles.css explains at length beside the rules. `aria-hidden` because the
  // brief requires it stay decorative: "it must not resemble another button, reward panel,
  // advertisement, or required gameplay element", and an image with no accessible name in a rail
  // of real information is the honest way to say so.
  //
  // NO SET, NO REGION. A build whose server predates the rotation sends no `set`, and an empty
  // 180px box with no picture in it would be a worse rail than the one that is merely unfinished.
  // The phone case is CSS's, not this function's: below 900px Zone 3 is a drawer, and the
  // stylesheet is the one authority for that boundary.
  function regionThreeHtml() {
    const DC = window.DailyChallenge;
    const st = DC && DC.state();
    const id = st && st.set && typeof st.set.id === 'string' ? st.set.id : '';
    if (!id) return '';
    return '<div class="home-region-3" aria-hidden="true" data-challenge-set="' +
           escapeHtml(id) + '"></div>';
  }

  function render(opts) {
    const quiet = !!(opts && opts.quiet);
    window.UI.setSession(null);
    window.UIShell.resetHud();
    // With no state this hides the fight HUD and clears body.in-bout -- Home is emphatically not a
    // bout, and the site nav in the brand bar has to be reachable from it.
    H.renderHud();
    document.body.dataset.screen = 'home';

    // `is-returning` is the same signal the explainer folds on — a bout was started on this device.
    // It picks the art treatment: a first visit gets the masthead crop beside a headline that is
    // the whole point of the screen, and the tutorial keeps the room; somebody who has played gets
    // the full band, on a screen that no longer needs to explain itself.
    workspaceEl().innerHTML = `
      <div class="screen home-screen${hasPlayed() ? ' is-returning' : ''}">
        ${heroHtml()}
        ${challengeHtml()}
        ${explainerHtml()}
        ${cornerHtml()}
        ${exploreHtml()}
        ${artHtml()}
      </div>`;

    // Zone 3 keeps what it is for on a landing screen: the ways to start something that is not a
    // solo bout, plus your own record. The challenge is NOT here -- it is in Zone 2 above.
    const ctx = window.UIShell.rail();
    const hist = window.UIShell.railHistory();
    const online = H.onlineRailHtml();
    // Same three-panel order the setup rail uses, minus the challenge, which lives in Zone 2 here.
    // The record under the credits, and both above the way out to another player.
    const rail = H.creditRailHtml() + H.recordRailHtml() + online + regionThreeHtml();
    if (ctx) ctx.innerHTML = '';
    if (hist) hist.innerHTML = rail;
    // The label is what a phone player sees, because the rail collapses there and the toggle is
    // all that shows. Named for what is actually behind it -- on a build with no relay that is
    // only the credit panel, and calling that "Play online" would be a lie about a feature the
    // build does not have.
    window.UIShell.setRailVisible(!!rail, online ? 'Play online' : 'Your credits');

    // ONE PRIMARY ACTION, in the place this shell always puts one. The audit's first acceptance
    // criterion is that a first visit offers exactly one, before any configuration.
    window.UIShell.setCommand({
      instruction: TAGLINE,
      primary: { id: 'home-play', label: 'Play vs AI', onClick: toPlay }
    });

    // THE PREFERENCE IS WRITTEN ON A CLICK, not on the element's own toggle event. `ontoggle`
    // was the obvious binding and it is wrong: it fires when the open attribute is set, which
    // includes the moment this render puts it there, so the first paint wrote its own state back
    // as if the player had chosen it -- and a returning player therefore never saw it folded.
    // A click on the summary is unambiguously a person. It runs BEFORE the element flips, so the
    // state being asked for is the opposite of the current one.
    const fold = workspaceEl().querySelector('.home-loop-fold');
    if (fold) {
      const summary = fold.querySelector('summary');
      if (summary) summary.addEventListener('click', function () { setLoopOpen(!fold.open); });
    }

    if (!quiet) {
      window.UIShell.scrollToTop();
      announce('Home');
    }
    return true;
  }

  function toPlay() {
    document.body.dataset.screen = 'play';
    H.renderSetup();
  }

  // A CHALLENGE TIER, STARTED FROM HOME. Two steps because startDailyChallenge reads the player's
  // deck out of #p1-deck, which only the setup screen builds -- so Play is rendered first and the
  // tier started against whatever deck it defaulted to. That deck is never one of today's three:
  // applyDailyDefault (see js/ui.js) moves it aside on days the podium would have barred it, which
  // is precisely what lets Home draw enabled rows without a select to check them against.
  //
  // If it refuses anyway, the player is left on the Play screen with the panel's own note
  // explaining why. That is the designed fallback, not an error path.
  function playChallenge(tier) {
    toPlay();
    return window.UI.startDailyChallenge(tier);
  }

  // The account answer arrives after main.js has already drawn this screen (initSetupAccount is
  // not awaited, on purpose), so Your Corner is first rendered in the "no backend" state. See the
  // note beside the publisher in js/ui.js for why this is its own step rather than a second
  // subscriber to account.statusKnown.
  onFlow('home.accountKnown', function () {
    if (!isShowing()) return false;
    render({ quiet: true });
    return true;
  });

  return { render, playChallenge, isShowing, toPlay, markPlayed, setLoopOpen, loopOpen, hasPlayed,
           hasWonAI, markWonAI,
           // The pitch, for the one other screen that has to say it. See the constant.
           TAGLINE: TAGLINE };
})();
